import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { BadgeCheck, ShieldBan, ShieldCheck } from 'lucide-react'
import { env } from '@/lib/env'
import { endOfDayParam, formatDateTimeCompact, startOfDayParam } from '@/lib/datetime'
import { fetchAllPages, type ExportColumn } from '@/lib/export'
import { queryKeys } from '@/lib/query-client'
import type { AdminUser, PeopleFilters } from '@/types/api'
import { useAdminMutation } from '@/hooks/useAdminMutation'
import { useTableState } from '@/hooks/useTableState'
import { PageHeader } from '@/components/layout/PageHeader'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DataTable } from '@/components/ui/DataTable'
import { ExportMenu } from '@/components/ui/ExportMenu'
import { FilterBar, FilterDate, FilterSelect } from '@/components/ui/FilterBar'
import { Pagination } from '@/components/ui/Pagination'
import { RowActions, StackedCell } from '@/components/ui/RowActions'
import { SearchInput } from '@/components/ui/SearchInput'
import { Badge, StatusBadge } from '@/components/ui/StatusBadge'
import { activateUser, fetchUsers, suspendUser, verifyUserPhone } from './api'

type UserAction = 'suspend' | 'activate' | 'verify-phone'

interface PendingAction {
  user: AdminUser
  action: UserAction
}

const DEFAULT_FILTERS = {
  search: '',
  enabled: '',
  phoneVerified: '',
  createdFrom: '',
  createdTo: '',
}

const ACTION_COPY: Record<UserAction, { title: string; confirm: string; tone: 'primary' | 'danger'; body: string }> = {
  suspend: {
    title: 'Suspend this passenger?',
    confirm: 'Suspend passenger',
    tone: 'danger',
    body: 'They will be signed out of the mobile app and will not be able to book new trips until the account is reactivated.',
  },
  activate: {
    title: 'Reactivate this passenger?',
    confirm: 'Reactivate passenger',
    tone: 'primary',
    body: 'They will be able to sign in and book trips again.',
  },
  'verify-phone': {
    title: 'Mark this phone number as verified?',
    confirm: 'Verify phone',
    tone: 'primary',
    body: 'Use this only when the number has been confirmed by another means — it bypasses the app’s own verification step.',
  },
}

export function UsersPage() {
  const table = useTableState({ defaultSort: 'createdAt', defaultFilters: DEFAULT_FILTERS })
  const [pending, setPending] = useState<PendingAction | null>(null)

  useEffect(() => {
    document.title = `Users · ${env.appName} Operations`
  }, [])

  const filters = useMemo<PeopleFilters>(
    () => ({
      page: table.page,
      size: table.size,
      sort: table.sort,
      direction: table.direction,
      search: table.filters.search || undefined,
      enabled: table.filters.enabled === '' ? undefined : table.filters.enabled === 'true',
      phoneVerified: table.filters.phoneVerified === '' ? undefined : table.filters.phoneVerified === 'true',
      createdFrom: table.filters.createdFrom ? startOfDayParam(table.filters.createdFrom) : undefined,
      createdTo: table.filters.createdTo ? endOfDayParam(table.filters.createdTo) : undefined,
    }),
    [table.page, table.size, table.sort, table.direction, table.filters],
  )

  const usersQuery = useQuery({
    queryKey: queryKeys.users.list(filters),
    queryFn: () => fetchUsers(filters),
    placeholderData: (previous) => previous,
  })

  const closeDialog = () => setPending(null)

  const statusMutation = useAdminMutation({
    mutationFn: ({ user, action }: PendingAction) =>
      action === 'suspend'
        ? suspendUser(user.id)
        : action === 'activate'
          ? activateUser(user.id)
          : verifyUserPhone(user.id),
    successTitle: 'Passenger updated',
    invalidate: [queryKeys.users.all, queryKeys.dashboard.all],
    errorTitle: 'Could not update the passenger',
    onSuccess: closeDialog,
  })

  const columns = useMemo<ColumnDef<AdminUser, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Passenger',
        meta: { sortKey: 'name', mobileLabel: 'Passenger' },
        cell: ({ row }) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', minWidth: 0 }}>
            <Avatar name={row.original.name} src={row.original.profileImageUrl} size="sm" />
            <StackedCell primary={row.original.name ?? 'Unnamed'} secondary={`ID ${row.original.id}`} />
          </div>
        ),
      },
      {
        id: 'contact',
        header: 'Contact',
        meta: { sortKey: 'email', mobileLabel: 'Contact' },
        cell: ({ row }) => (
          <StackedCell primary={row.original.email ?? '—'} secondary={row.original.phone ?? 'No phone number'} />
        ),
      },
      {
        id: 'createdAt',
        header: 'Registered',
        meta: { sortKey: 'createdAt', mobileLabel: 'Registered', hideOnMobile: true },
        cell: ({ row }) => formatDateTimeCompact(row.original.createdAt),
      },
      {
        id: 'phoneVerified',
        header: 'Phone',
        meta: { sortKey: 'phoneVerified', mobileLabel: 'Phone' },
        cell: ({ row }) =>
          row.original.phoneVerified ? (
            <Badge tone="success" dot>
              Verified
            </Badge>
          ) : (
            <Badge tone="warning" dot>
              Unverified
            </Badge>
          ),
      },
      {
        id: 'enabled',
        header: 'Account',
        meta: { sortKey: 'enabled', mobileLabel: 'Account' },
        cell: ({ row }) => <StatusBadge status={row.original.enabled ? 'ACTIVE' : 'SUSPENDED'} />,
      },
      {
        id: 'actions',
        header: 'Actions',
        meta: { align: 'right', sticky: true, width: '260px' },
        cell: ({ row }) => (
          <RowActions>
            {!row.original.phoneVerified ? (
              <Button
                size="sm"
                variant="secondary"
                iconLeft={<BadgeCheck aria-hidden="true" />}
                onClick={() => setPending({ user: row.original, action: 'verify-phone' })}
              >
                Verify phone
              </Button>
            ) : null}

            {row.original.enabled ? (
              <Button
                size="sm"
                variant="danger"
                iconLeft={<ShieldBan aria-hidden="true" />}
                onClick={() => setPending({ user: row.original, action: 'suspend' })}
              >
                Suspend
              </Button>
            ) : (
              <Button
                size="sm"
                variant="subtle"
                iconLeft={<ShieldCheck aria-hidden="true" />}
                onClick={() => setPending({ user: row.original, action: 'activate' })}
              >
                Reactivate
              </Button>
            )}
          </RowActions>
        ),
      },
    ],
    [],
  )

  const exportColumns = useMemo<ExportColumn<AdminUser>[]>(
    () => [
      { header: 'ID', value: (row) => row.id },
      { header: 'Name', value: (row) => row.name ?? '' },
      { header: 'Email', value: (row) => row.email ?? '' },
      { header: 'Phone', value: (row) => row.phone ?? '' },
      { header: 'Registered', value: (row) => formatDateTimeCompact(row.createdAt, '') },
      { header: 'Phone verified', value: (row) => (row.phoneVerified ? 'Yes' : 'No') },
      { header: 'Email verified', value: (row) => (row.emailVerified ? 'Yes' : 'No') },
      { header: 'Status', value: (row) => (row.enabled ? 'Active' : 'Suspended') },
      { header: 'Sign-in method', value: (row) => row.authProvider ?? '' },
    ],
    [],
  )

  const page = usersQuery.data
  const copy = pending ? ACTION_COPY[pending.action] : null

  return (
    <>
      <PageHeader
        title="Passengers"
        description="Everyone who books through the Drive For Me app. Suspend an account to block new bookings, or confirm a phone number that could not be verified in the app."
      />

      <Card flush>
        <FilterBar
          search={
            <SearchInput
              value={table.filters.search}
              onChange={(value) => table.setFilter('search', value)}
              placeholder="Search by name, email or phone…"
              label="Search passengers"
            />
          }
          actions={
            <ExportMenu
              columns={exportColumns}
              baseName="drive-for-me-passengers"
              title="Drive For Me — Passengers"
              // Walks every page with the filters currently applied, so the file
              // matches the view rather than just its first page.
              fetchRows={() =>
                fetchAllPages((exportPage, exportSize) =>
                  fetchUsers({ ...filters, page: exportPage, size: exportSize }),
                )
              }
            />
          }
          activeFilterCount={table.activeFilterCount}
          onReset={table.resetFilters}
        >
          <FilterSelect
            label="Account"
            value={table.filters.enabled}
            onChange={(value) => table.setFilter('enabled', value)}
            options={[
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Suspended' },
            ]}
          />
          <FilterSelect
            label="Phone"
            value={table.filters.phoneVerified}
            onChange={(value) => table.setFilter('phoneVerified', value)}
            options={[
              { value: 'true', label: 'Verified' },
              { value: 'false', label: 'Unverified' },
            ]}
          />
          <FilterDate
            label="From"
            value={table.filters.createdFrom}
            onChange={(value) => table.setFilter('createdFrom', value)}
            max={table.filters.createdTo || undefined}
          />
          <FilterDate
            label="To"
            value={table.filters.createdTo}
            onChange={(value) => table.setFilter('createdTo', value)}
            min={table.filters.createdFrom || undefined}
          />
        </FilterBar>

        <DataTable
          caption="Drive For Me passengers"
          data={page?.content ?? []}
          columns={columns}
          getRowId={(row) => String(row.id)}
          isLoading={usersQuery.isLoading}
          isFetching={usersQuery.isFetching && !usersQuery.isLoading}
          error={usersQuery.isError ? usersQuery.error : undefined}
          onRetry={() => void usersQuery.refetch()}
          sort={table.sort}
          direction={table.direction}
          onSortChange={table.setSort}
          emptyTitle={table.activeFilterCount > 0 ? 'No passengers match these filters' : 'No passengers yet'}
          emptyDescription={
            table.activeFilterCount > 0
              ? 'Try widening the date range or clearing a filter.'
              : 'Passenger accounts appear here once people sign up in the app.'
          }
          emptyAction={
            table.activeFilterCount > 0 ? (
              <Button variant="secondary" onClick={table.resetFilters}>
                Clear filters
              </Button>
            ) : null
          }
        />

        {page && page.totalElements > 0 ? (
          <Pagination
            page={page.page}
            size={page.size}
            totalElements={page.totalElements}
            totalPages={page.totalPages}
            onPageChange={table.setPage}
            onSizeChange={table.setSize}
            noun="passenger"
            disabled={usersQuery.isFetching}
          />
        ) : null}
      </Card>

      <ConfirmDialog
        open={pending !== null}
        title={copy?.title ?? ''}
        message={
          pending ? (
            <>
              <strong>{pending.user.name ?? `Passenger #${pending.user.id}`}</strong>
              {pending.user.email ? ` (${pending.user.email})` : ''}. {copy?.body}
            </>
          ) : (
            ''
          )
        }
        confirmLabel={copy?.confirm}
        tone={copy?.tone}
        loading={statusMutation.isPending}
        onConfirm={() => pending && statusMutation.mutate(pending)}
        onCancel={closeDialog}
      />
    </>
  )
}
